// src/pages/admin/KYC.jsx - FIXED URL RESOLUTION
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  TablePagination,
  Link,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
  Collapse,
  Tabs,
  Tab,
  Badge,
  Skeleton,
} from '@mui/material';
import {
  VerifiedUser,
  Refresh,
  CheckCircle,
  Cancel,
  Pending,
  Visibility,
  Download,
  MoreVert,
  Description,
  Assignment,
  Store,
  Person,
  Email,
  Phone,
  ExpandMore,
  ExpandLess,
  Warning,
  Info,
  Check,
  Close,
} from '@mui/icons-material';

// ✅ FIX: Correct API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL 
  ? process.env.REACT_APP_API_URL.replace(/\/api$/, '') 
  : 'https://api.goscitech.com';

// ✅ FIX: Helper function to resolve document URLs correctly
const resolveDocumentUrl = (url) => {
  if (!url) return null;
  
  // If it's already a full URL with https:// or http://
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Clean the URL - remove any double slashes or incorrect prefixes
  let cleanUrl = url;
  
  // Remove any leading .goscitech.com or similar from the URL
  cleanUrl = cleanUrl.replace(/^\.scitech-solution\.com/i, '');
  cleanUrl = cleanUrl.replace(/^\.scitech-solution\.com\/api/i, '');
  cleanUrl = cleanUrl.replace(/^\/\/scitech-solution\.com/i, '');
  cleanUrl = cleanUrl.replace(/^scitech-solution\.com/i, '');
  
  // Ensure the URL starts with /uploads
  if (!cleanUrl.startsWith('/uploads') && !cleanUrl.startsWith('uploads')) {
    // If it doesn't start with /uploads, check if it has uploads in the path
    const uploadsIndex = cleanUrl.indexOf('/uploads');
    if (uploadsIndex !== -1) {
      cleanUrl = cleanUrl.substring(uploadsIndex);
    } else {
      // If no uploads path found, assume it's a direct file path
      cleanUrl = cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
    }
  }
  
  // Ensure the URL starts with /
  if (!cleanUrl.startsWith('/')) {
    cleanUrl = `/${cleanUrl}`;
  }
  
  // Remove any duplicate /api/ in the path
  cleanUrl = cleanUrl.replace(/\/api\/uploads/g, '/uploads');
  cleanUrl = cleanUrl.replace(/\/api\/kyc/g, '/kyc');
  
  // Final URL construction
  const finalUrl = `${API_BASE_URL}${cleanUrl}`;
  
  console.log('Resolved URL:', {
    original: url,
    cleaned: cleanUrl,
    final: finalUrl
  });
  
  return finalUrl;
};

const AdminKYC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // ============================================================
  // STATE
  // ============================================================
  const [kycRequests, setKycRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog & Action State
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // ============================================================
  // FETCH DATA - FIXED WITH PROPER DOCUMENT URLS
  // ============================================================
  const fetchKYCRequests = async (currentPage = page, currentTab = tabValue) => {
    setLoading(true);
    setError(null);
    try {
      const statusMap = ['pending', 'verified', 'rejected'];
      const status = statusMap[currentTab] || 'pending';
      
      console.log(`Fetching KYC requests with status: ${status}, page: ${currentPage + 1}`);
      
      let response;
      try {
        // Try the primary KYC endpoint first
        response = await adminApi.getKYCRequests({
          page: currentPage + 1,
          limit: rowsPerPage,
          status: status,
        });
      } catch (primaryError) {
        console.warn('Primary KYC endpoint failed, using vendors endpoint...');
        
        // Use vendors endpoint as fallback
        let isVerified;
        if (status === 'pending') {
          isVerified = false;
        } else if (status === 'verified') {
          isVerified = true;
        } else if (status === 'rejected') {
          isVerified = false;
        }
        
        const vendorsResponse = await adminApi.getVendors({
          page: currentPage + 1,
          limit: rowsPerPage,
          isVerified: isVerified,
        });
        
        const vendors = vendorsResponse.data?.vendors || [];
        
        // Transform to KYC format with proper document URLs
        let kycData = vendors.map(v => ({
          vendorId: v.id,
          storeName: v.storeName,
          ownerName: v.User?.name || 'N/A',
          ownerEmail: v.User?.email || 'N/A',
          status: v.isVerified ? 'verified' : 'pending',
          submittedAt: v.createdAt,
          hasLicense: !!(v.licenseUrl),
          hasTaxId: !!(v.taxIdUrl),
          // ✅ FIX: Resolve document URLs properly
          licenseUrl: v.licenseUrl ? resolveDocumentUrl(v.licenseUrl) : null,
          taxIdUrl: v.taxIdUrl ? resolveDocumentUrl(v.taxIdUrl) : null,
          isActive: v.isActive,
          User: v.User || {},
          businessPhone: v.businessPhone,
          businessEmail: v.businessEmail,
          businessAddress: v.businessAddress,
          taxId: v.taxId,
          createdAt: v.createdAt,
          storeLogo: v.storeLogo,
        }));
        
        // Filter by status properly
        let filteredData = [];
        if (status === 'pending') {
          filteredData = kycData.filter(v => !v.isVerified && v.isActive);
        } else if (status === 'verified') {
          filteredData = kycData.filter(v => v.isVerified && v.isActive);
        } else if (status === 'rejected') {
          filteredData = kycData.filter(v => !v.isVerified && !v.isActive);
        }
        
        setKycRequests(filteredData);
        setTotalCount(filteredData.length);
        setLoading(false);
        return;
      }
      
      // Process primary response
      let data = response.data || response;
      let requests = data.kycRequests || data.requests || data || [];
      
      if (!Array.isArray(requests)) {
        requests = [];
      }
      
      // ✅ FIX: Resolve document URLs for each request
      const resolvedRequests = requests.map(req => ({
        ...req,
        licenseUrl: req.licenseUrl ? resolveDocumentUrl(req.licenseUrl) : null,
        taxIdUrl: req.taxIdUrl ? resolveDocumentUrl(req.taxIdUrl) : null,
      }));
      
      // Filter by status
      let filteredRequests = resolvedRequests;
      if (status === 'rejected') {
        filteredRequests = resolvedRequests.filter(r => 
          !r.isActive && 
          (r.status === 'pending' || r.status === 'rejected' || !r.status)
        );
      }
      
      setKycRequests(filteredRequests);
      setTotalCount(data.total || filteredRequests.length || 0);
      
    } catch (error) {
      console.error('Error fetching KYC requests:', error);
      setError('Failed to load KYC verification requests. Please try again.');
      setKycRequests([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchKYCRequests(0, tabValue);
  }, [tabValue]);

  useEffect(() => {
    if (page > 0) {
      fetchKYCRequests(page, tabValue);
    }
  }, [page]);

  // ============================================================
  // HANDLERS
  // ============================================================
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleMenuOpen = (event, request) => {
    setAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    setOpenDetailsDialog(true);
    handleMenuClose();
  };

  const handleExpandRow = (vendorId) => {
    setExpandedRow(expandedRow === vendorId ? null : vendorId);
  };

  const handleApproveKYC = async () => {
    if (!selectedRequest) return;
    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      await adminApi.approveKYC(selectedRequest.vendorId);
      setSuccess(`✅ ${selectedRequest.storeName || 'Vendor'} has been verified successfully!`);
      setOpenApproveDialog(false);
      fetchKYCRequests(page, tabValue);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to approve KYC.');
    } finally {
      setIsProcessing(false);
      setSelectedRequest(null);
    }
  };

  const handleRejectKYC = async () => {
    if (!selectedRequest) return;
    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      await adminApi.rejectKYC(selectedRequest.vendorId);
      setSuccess(`❌ ${selectedRequest.storeName || 'Vendor'}'s documents have been rejected.`);
      setOpenRejectDialog(false);
      fetchKYCRequests(page, tabValue);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to reject KYC.');
    } finally {
      setIsProcessing(false);
      setSelectedRequest(null);
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchKYCRequests(page, tabValue);
  };

  // ============================================================
  // UI HELPERS
  // ============================================================
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    switch (statusLower) {
      case 'verified':
      case 'approved':
        return 'success';
      case 'rejected':
      case 'declined':
        return 'error';
      case 'pending':
      default:
        return 'warning';
    }
  };

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase() || '';
    switch (statusLower) {
      case 'verified':
      case 'approved':
        return <CheckCircle />;
      case 'rejected':
      case 'declined':
        return <Cancel />;
      default:
        return <Pending />;
    }
  };

  const getStatusLabel = (status) => {
    const statusLower = status?.toLowerCase() || '';
    switch (statusLower) {
      case 'verified':
      case 'approved':
        return 'Verified';
      case 'rejected':
      case 'declined':
        return 'Rejected';
      default:
        return 'Pending';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Count by status
  const getCountByStatus = (status) => {
    return kycRequests.filter(r => {
      const rStatus = r.status?.toLowerCase() || '';
      if (status === 'pending') return rStatus === 'pending' || !rStatus;
      if (status === 'verified') return rStatus === 'verified' || rStatus === 'approved';
      if (status === 'rejected') return rStatus === 'rejected' || rStatus === 'declined';
      return false;
    }).length;
  };

  // ============================================================
  // RENDER MOBILE CARD
  // ============================================================
  const renderMobileCard = (req) => {
    const isExpanded = expandedRow === req.vendorId;

    return (
      <Card key={req.vendorId || req.id} sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar 
                  sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                  src={req.storeLogo ? resolveDocumentUrl(req.storeLogo) : undefined}
                >
                  {req.storeName?.[0]?.toUpperCase() || 'V'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {req.storeName || 'Unknown Vendor'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Owner: {req.ownerName || req.User?.name || 'N/A'}
                  </Typography>
                </Box>
              </Stack>
              <Chip
                icon={getStatusIcon(req.status)}
                label={getStatusLabel(req.status)}
                size="small"
                color={getStatusColor(req.status)}
              />
            </Stack>

            {/* Documents */}
            <Stack direction="row" spacing={1}>
              {(req.hasLicense || req.licenseUrl) && (
                <Chip 
                  icon={<Description />} 
                  label="License" 
                  size="small" 
                  color="info" 
                  variant="outlined" 
                />
              )}
              {(req.hasTaxId || req.taxIdUrl) && (
                <Chip 
                  icon={<Assignment />} 
                  label="Tax ID" 
                  size="small" 
                  color="info" 
                  variant="outlined" 
                />
              )}
            </Stack>

            {/* Date */}
            <Typography variant="caption" color="text.secondary">
              Submitted: {formatDate(req.submittedAt || req.createdAt)}
            </Typography>

            {/* Actions */}
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => {
                  setSelectedRequest(req);
                  setOpenDetailsDialog(true);
                }}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
              >
                Review
              </Button>
              {getStatusLabel(req.status) === 'Pending' && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<Check />}
                    onClick={() => {
                      setSelectedRequest(req);
                      setOpenApproveDialog(true);
                    }}
                    sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<Close />}
                    onClick={() => {
                      setSelectedRequest(req);
                      setOpenRejectDialog(true);
                    }}
                    sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                  >
                    Reject
                  </Button>
                </>
              )}
              <IconButton size="small" onClick={() => handleExpandRow(req.vendorId || req.id)}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Stack>

            {/* Expanded Details */}
            <Collapse in={isExpanded}>
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight="bold">Vendor Details</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Person fontSize="small" color="action" />
                  <Typography variant="body2">{req.ownerName || req.User?.name || 'N/A'}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Email fontSize="small" color="action" />
                  <Typography variant="body2">{req.ownerEmail || req.User?.email || 'N/A'}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Phone fontSize="small" color="action" />
                  <Typography variant="body2">{req.User?.phone || req.businessPhone || 'N/A'}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Store fontSize="small" color="action" />
                  <Typography variant="body2">{req.storeName || 'N/A'}</Typography>
                </Stack>
                {req.taxId && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Assignment fontSize="small" color="action" />
                    <Typography variant="body2">Tax ID: {req.taxId}</Typography>
                  </Stack>
                )}
              </Stack>
            </Collapse>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // ============================================================
  // RENDER
  // ============================================================
  if (loading && kycRequests.length === 0 && page === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Vendor KYC & Verification
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review vendor submitted documents and verify their store
            </Typography>
          </Box>
          <Button 
            variant="outlined" 
            startIcon={<Refresh />} 
            onClick={handleRefresh} 
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Stack>
      </Paper>

      {/* Status Messages */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            '& .MuiTab-root': {
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              minHeight: { xs: 40, sm: 48 },
              textTransform: 'none',
              fontWeight: 600,
            },
          }}
        >
          <Tab 
            label={
              <Badge 
                badgeContent={getCountByStatus('pending')} 
                color="warning"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
              >
                Pending
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge 
                badgeContent={getCountByStatus('verified')} 
                color="success"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
              >
                Verified
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge 
                badgeContent={getCountByStatus('rejected')} 
                color="error"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
              >
                Rejected
              </Badge>
            } 
          />
        </Tabs>
      </Paper>

      {/* KYC Table / Cards */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {isMobile ? (
          // Mobile Card View
          <Box sx={{ p: 1.5 }}>
            {kycRequests.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No {tabValue === 0 ? 'pending' : tabValue === 1 ? 'verified' : 'rejected'} KYC verification requests
                </Typography>
              </Paper>
            ) : (
              kycRequests.map((req) => renderMobileCard(req))
            )}
          </Box>
        ) : (
          // Desktop Table View
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Vendor</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Documents</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Submitted</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Status</Typography></TableCell>
                    <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Actions</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kycRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          No {tabValue === 0 ? 'pending' : tabValue === 1 ? 'verified' : 'rejected'} KYC verification requests
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    kycRequests.map((req) => (
                      <TableRow key={req.vendorId || req.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar 
                              sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                              src={req.storeLogo ? resolveDocumentUrl(req.storeLogo) : undefined}
                            >
                              {req.storeName?.[0]?.toUpperCase() || 'V'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="600">
                                {req.storeName || 'Unknown Vendor'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Owner: {req.ownerName || req.User?.name || 'N/A'}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            {(req.hasLicense || req.licenseUrl) && (
                              <Chip 
                                icon={<Description sx={{ fontSize: 14 }} />} 
                                label="License" 
                                size="small" 
                                color="info" 
                                variant="outlined" 
                              />
                            )}
                            {(req.hasTaxId || req.taxIdUrl) && (
                              <Chip 
                                icon={<Assignment sx={{ fontSize: 14 }} />} 
                                label="Tax ID" 
                                size="small" 
                                color="info" 
                                variant="outlined" 
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(req.submittedAt || req.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(req.status)}
                            label={getStatusLabel(req.status)}
                            size="small"
                            color={getStatusColor(req.status)}
                            variant={req.status === 'pending' ? 'outlined' : 'filled'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, req)}>
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* ============================================================ */}
      {/* CONTEXT MENU */}
      {/* ============================================================ */}
      <Menu 
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onClose={handleMenuClose}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 180 }
        }}
      >
        <MenuItem onClick={handleViewDetails}>
          <Visibility fontSize="small" sx={{ mr: 1.5 }} /> View Documents
        </MenuItem>
        {getStatusLabel(selectedRequest?.status) === 'Pending' && (
          <>
            <Divider />
            <MenuItem onClick={() => { setOpenApproveDialog(true); handleMenuClose(); }} sx={{ color: 'success.main' }}>
              <CheckCircle fontSize="small" sx={{ mr: 1.5 }} /> Approve Vendor
            </MenuItem>
            <MenuItem onClick={() => { setOpenRejectDialog(true); handleMenuClose(); }} sx={{ color: 'error.main' }}>
              <Cancel fontSize="small" sx={{ mr: 1.5 }} /> Reject Documents
            </MenuItem>
          </>
        )}
        {getStatusLabel(selectedRequest?.status) === 'Verified' && (
          <MenuItem disabled>
            <Info fontSize="small" sx={{ mr: 1.5 }} /> Already Verified
          </MenuItem>
        )}
        {getStatusLabel(selectedRequest?.status) === 'Rejected' && (
          <MenuItem disabled>
            <Info fontSize="small" sx={{ mr: 1.5 }} /> Already Rejected
          </MenuItem>
        )}
      </Menu>

      {/* ============================================================ */}
      {/* VIEW DETAILS DIALOG - WITH PROPER DOCUMENT LINKS */}
      {/* ============================================================ */}
      <Dialog 
        open={openDetailsDialog} 
        onClose={() => setOpenDetailsDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <VerifiedUser color="primary" />
            <Typography variant="h6">Review Vendor Documents</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Stack spacing={3}>
              {/* Vendor Info */}
              <Box>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  Vendor Information
                </Typography>
                <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Store Name</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedRequest.storeName || 'N/A'}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Owner</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedRequest.ownerName || selectedRequest.User?.name || 'N/A'}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedRequest.ownerEmail || selectedRequest.User?.email || 'N/A'}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Phone</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedRequest.User?.phone || selectedRequest.businessPhone || 'N/A'}</Typography>
                    </Stack>
                    {selectedRequest.taxId && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Tax ID</Typography>
                        <Typography variant="body2" fontWeight="600">{selectedRequest.taxId}</Typography>
                      </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip
                        icon={getStatusIcon(selectedRequest.status)}
                        label={getStatusLabel(selectedRequest.status)}
                        size="small"
                        color={getStatusColor(selectedRequest.status)}
                      />
                    </Stack>
                  </Stack>
                </Paper>
              </Box>

              <Divider />

              {/* Documents */}
              <Box>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  Submitted Documents
                </Typography>
                
                {/* Business License */}
                {(selectedRequest.hasLicense || selectedRequest.licenseUrl) && (
                  <Paper sx={{ p: 2, mt: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Description color="info" />
                        <Box>
                          <Typography variant="body2" fontWeight="600">Business License</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Uploaded: {formatDate(selectedRequest.submittedAt || selectedRequest.createdAt)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Button 
                        variant="outlined" 
                        startIcon={<Download />} 
                        href={selectedRequest.licenseUrl} 
                        target="_blank" 
                        size="small"
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                      >
                        View
                      </Button>
                    </Stack>
                  </Paper>
                )}

                {/* Tax ID */}
                {(selectedRequest.hasTaxId || selectedRequest.taxIdUrl) && (
                  <Paper sx={{ p: 2, mt: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Assignment color="info" />
                        <Box>
                          <Typography variant="body2" fontWeight="600">Tax ID / TIN Document</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Uploaded: {formatDate(selectedRequest.submittedAt || selectedRequest.createdAt)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Button 
                        variant="outlined" 
                        startIcon={<Download />} 
                        href={selectedRequest.taxIdUrl} 
                        target="_blank" 
                        size="small"
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                      >
                        View
                      </Button>
                    </Stack>
                  </Paper>
                )}

                {!selectedRequest.hasLicense && !selectedRequest.hasTaxId && 
                 !selectedRequest.licenseUrl && !selectedRequest.taxIdUrl && (
                  <Paper sx={{ p: 3, mt: 1, textAlign: 'center', bgcolor: 'warning.light' }}>
                    <Warning color="warning" />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No documents have been uploaded by this vendor yet.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenDetailsDialog(false)} fullWidth={isMobile}>Close</Button>
          {getStatusLabel(selectedRequest?.status) === 'Pending' && (
            <>
              <Button 
                variant="contained" 
                color="success" 
                onClick={() => { setOpenDetailsDialog(false); setOpenApproveDialog(true); }}
                fullWidth={isMobile}
                startIcon={<Check />}
              >
                Approve
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                onClick={() => { setOpenDetailsDialog(false); setOpenRejectDialog(true); }}
                fullWidth={isMobile}
                startIcon={<Close />}
              >
                Reject
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* APPROVE DIALOG */}
      {/* ============================================================ */}
      <Dialog 
        open={openApproveDialog} 
        onClose={() => setOpenApproveDialog(false)} 
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle sx={{ color: 'success.main' }}>
          <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} /> 
          Verify Vendor
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to verify <strong>{selectedRequest?.storeName || 'this vendor'}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            ✅ They will be able to start selling immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenApproveDialog(false)} disabled={isProcessing} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            onClick={handleApproveKYC} 
            disabled={isProcessing}
            fullWidth={isMobile}
            startIcon={isProcessing ? <CircularProgress size={20} /> : <Check />}
          >
            {isProcessing ? 'Processing...' : 'Verify Vendor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* REJECT DIALOG */}
      {/* ============================================================ */}
      <Dialog 
        open={openRejectDialog} 
        onClose={() => setOpenRejectDialog(false)} 
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 3, m: { xs: 1, sm: 2 } }
        }}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          <Cancel sx={{ mr: 1, verticalAlign: 'middle' }} /> 
          Reject Documents
        </DialogTitle>
        <DialogContent>
          <Typography>
            Reject the documents submitted by <strong>{selectedRequest?.storeName || 'this vendor'}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            ❌ This will notify the vendor to re-upload correct documents.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenRejectDialog(false)} disabled={isProcessing} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleRejectKYC} 
            disabled={isProcessing}
            fullWidth={isMobile}
            startIcon={isProcessing ? <CircularProgress size={20} /> : <Close />}
          >
            {isProcessing ? 'Processing...' : 'Reject Documents'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminKYC;